import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private isLoaded = false;
  private loadingPromise: Promise<void> | null = null;

  constructor() {}

  /**
   * Load the Google Maps API and return an observable that resolves when it's loaded
   */
  loadGoogleMaps(): Observable<void> {
    // If already loaded, return immediately
    if (this.isLoaded && window.google && window.google.maps) {
      return of(undefined);
    }

    // If currently loading, return the existing promise
    if (this.loadingPromise) {
      return from(this.loadingPromise);
    }

    // Create and store the loading promise
    this.loadingPromise = new Promise<void>((resolve, reject) => {
      try {
        // Define callback for when Google Maps is loaded
        const callbackName = 'googleMapsLoaded_' + Math.random().toString(36).substring(2, 15);
        window[callbackName] = () => {
          this.isLoaded = true;
          resolve();
          delete window[callbackName];
        };

        // Create script element
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.defer = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.google.mapApiKey}&libraries=places,geometry,drawing,marker&callback=${callbackName}&loading=async`;

        // Handle script load errors
        script.onerror = error => {
          reject(new Error('Failed to load Google Maps API: ' + error.toString()));
        };

        // Add script to page
        document.head.appendChild(script);
      } catch (error) {
        reject(error);
      }
    });

    // Convert promise to observable
    return from(this.loadingPromise).pipe(
      catchError(error => {
        console.error('Error loading Google Maps:', error);
        this.loadingPromise = null;
        throw error;
      })
    );
  }
}
